import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Camera } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, apiErrorMessage } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import confetti from "canvas-confetti";

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [address, setAddress] = useState({ full_name: user?.name || "", phone: "", line1: "", suburb: "", state: "", postcode: "", country: "Australia" });
  const [fulfilmentMethod, setFulfilmentMethod] = useState("delivery");
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [creditBalance, setCreditBalance] = useState(0);
  const [applyCredit, setApplyCredit] = useState(0);
  const [couponCode, setCouponCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pickupInfo, setPickupInfo] = useState(null);

  useEffect(() => {
    if (user) api.get("/credits/balance").then((res) => setCreditBalance(res.data.balance));
  }, [user]);

  useEffect(() => {
    api.get("/settings/public").then((res) => setPickupInfo(res.data)).catch(() => {});
  }, []);

  if (!user) {
    return (
      <div className="container-lookers py-24 text-center" data-testid="checkout-login-required">
        <h1 className="font-heading font-extrabold text-3xl mb-4 text-neutral-900">Please Log In To Checkout</h1>
        <Link to="/login" state={{ from: { pathname: "/checkout" } }} className="bg-teal-600 hover:bg-charcoal text-white px-6 py-3 rounded-full text-xs font-heading font-bold uppercase transition-colors">Log In</Link>
      </div>
    );
  }

  if (items.length === 0) {
    return <div className="container-lookers py-24 text-center text-zinc-900">Your bag is empty. <Link to="/shop" className="underline text-teal-600">Shop frames</Link></div>;
  }

  const lensTotal = items.reduce((s, i) => s + (i.lens_price || 0), 0);
  const isPickup = fulfilmentMethod === "pickup";
  const shippingCost = isPickup
    ? 0
    : shippingMethod === "express"
    ? (pickupInfo?.express_shipping_rate ?? 22)
    : subtotal >= (pickupInfo?.free_shipping_threshold ?? 150)
    ? 0
    : (pickupInfo?.standard_shipping_rate ?? 12);
  const creditApplied = Math.min(Number(applyCredit) || 0, creditBalance, Math.max(subtotal + lensTotal + shippingCost - discount, 0));
  const orderTotal = Math.max(subtotal + lensTotal + shippingCost - discount - creditApplied, 0);

  const applyCoupon = async () => {
    if (!couponCode) return;
    try {
      const { data } = await api.post("/coupons/validate", null, { params: { code: couponCode, subtotal: subtotal + lensTotal } });
      setDiscount(data.discount);
      setAppliedCoupon(data.code);
      toast.success(`Coupon ${data.code} applied — you save ${money(data.discount)}`);
      confetti({
        particleCount: 90,
        spread: 75,
        startVelocity: 35,
        origin: { x: 0.5, y: 0.75 },
        colors: ["#09B9DE", "#0D9488", "#F43F5E", "#FFFFFF"],
      });
    } catch (e) {
      setDiscount(0);
      setAppliedCoupon(null);
      toast.error(apiErrorMessage(e));
    }
  };

  const handlePay = async () => {
    if (!address.full_name || !address.phone) {
      toast.error("Please provide your full name and phone number");
      return;
    }
    if (!isPickup && (!address.line1 || !address.suburb || !address.postcode)) {
      toast.error("Please complete your shipping address");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, lens_upgrade: !!i.lens_upgrade })),
        shipping_address: address,
        credit_applied: Number(applyCredit) || 0,
        coupon_code: appliedCoupon || null,
        shipping_method: shippingMethod,
        fulfilment_method: fulfilmentMethod,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout/create-session", payload);
      clearCart();
      if (data.no_payment_required) {
        navigate(`/payment/success?order_id=${data.order_id}`);
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-lookers py-12" data-testid="checkout-page">
      <h1 className="font-heading font-extrabold text-4xl mb-8 text-neutral-900">Checkout</h1>
      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-8">
          <div>
            <h2 className="font-heading text-xs uppercase tracking-wide mb-4 text-teal-700">How would you like your order?</h2>
            <div className="grid grid-cols-2 gap-3">
              <label className={`border rounded-sm px-4 py-3 text-sm cursor-pointer text-zinc-800 bg-white ${!isPickup ? "border-teal-600" : "border-zinc-200"}`}>
                <input type="radio" name="fulfilment" checked={!isPickup} onChange={() => setFulfilmentMethod("delivery")} className="accent-teal-600 mr-2" data-testid="fulfilment-delivery-radio" />
                Ship to me
              </label>
              {pickupInfo?.pickup_enabled && (
                <label className={`border rounded-sm px-4 py-3 text-sm cursor-pointer text-zinc-800 bg-white ${isPickup ? "border-teal-600" : "border-zinc-200"}`}>
                  <input type="radio" name="fulfilment" checked={isPickup} onChange={() => setFulfilmentMethod("pickup")} className="accent-teal-600 mr-2" data-testid="fulfilment-pickup-radio" />
                  Pick up in Perth — Free
                </label>
              )}
            </div>
            {isPickup && pickupInfo && (
              <div className="mt-3 text-xs text-zinc-500 bg-teal-50 border border-teal-100 rounded-sm p-4" data-testid="pickup-details">
                <div className="font-heading uppercase tracking-wide text-teal-800 mb-1">{pickupInfo.pickup_address}</div>
                <p>{pickupInfo.pickup_instructions}</p>
              </div>
            )}
          </div>

          <div>
            <h2 className="font-heading text-xs uppercase tracking-wide mb-4 text-teal-700">{isPickup ? "Contact Details" : "Shipping Address"}</h2>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Full name" value={address.full_name} onChange={(e) => setAddress({ ...address, full_name: e.target.value })} className="bg-white border border-zinc-200 px-3 py-2.5 text-sm col-span-2 text-zinc-900 placeholder:text-zinc-400 rounded-sm focus:border-teal-600 outline-none" data-testid="checkout-fullname" />
              <input placeholder="Phone" value={address.phone} onChange={(e) => setAddress({ ...address, phone: e.target.value })} className="bg-white border border-zinc-200 px-3 py-2.5 text-sm col-span-2 text-zinc-900 placeholder:text-zinc-400 rounded-sm focus:border-teal-600 outline-none" data-testid="checkout-phone" />
              {!isPickup && (
                <>
                  <input placeholder="Street address" value={address.line1} onChange={(e) => setAddress({ ...address, line1: e.target.value })} className="bg-white border border-zinc-200 px-3 py-2.5 text-sm col-span-2 text-zinc-900 placeholder:text-zinc-400 rounded-sm focus:border-teal-600 outline-none" data-testid="checkout-address-line1" />
                  <input placeholder="Suburb" value={address.suburb} onChange={(e) => setAddress({ ...address, suburb: e.target.value })} className="bg-white border border-zinc-200 px-3 py-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 rounded-sm focus:border-teal-600 outline-none" data-testid="checkout-suburb" />
                  <input placeholder="State" value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} className="bg-white border border-zinc-200 px-3 py-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 rounded-sm focus:border-teal-600 outline-none" data-testid="checkout-state" />
                  <input placeholder="Postcode" value={address.postcode} onChange={(e) => setAddress({ ...address, postcode: e.target.value })} className="bg-white border border-zinc-200 px-3 py-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 rounded-sm focus:border-teal-600 outline-none" data-testid="checkout-postcode" />
                  <input placeholder="Country" value={address.country} onChange={(e) => setAddress({ ...address, country: e.target.value })} className="bg-white border border-zinc-200 px-3 py-2.5 text-sm text-zinc-900 placeholder:text-zinc-400 rounded-sm focus:border-teal-600 outline-none" data-testid="checkout-country" />
                </>
              )}
            </div>
          </div>

          {!isPickup && (
            <div>
              <h2 className="font-heading text-xs uppercase tracking-wide mb-4 text-teal-700">Shipping Method</h2>
              <label className="flex items-center gap-2 text-sm mb-2 cursor-pointer text-zinc-800">
                <input type="radio" checked={shippingMethod === "standard"} onChange={() => setShippingMethod("standard")} className="accent-teal-600" data-testid="shipping-standard-radio" />
                Standard (free over $150)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer text-zinc-800">
                <input type="radio" checked={shippingMethod === "express"} onChange={() => setShippingMethod("express")} className="accent-teal-600" data-testid="shipping-express-radio" />
                Express
              </label>
            </div>
          )}

          {creditBalance > 0 && (
            <div>
              <h2 className="font-heading text-xs uppercase tracking-wide mb-4 text-teal-700">Lookers Credit — Balance {money(creditBalance)}</h2>
              <input
                type="number"
                max={Math.min(creditBalance, subtotal + lensTotal)}
                value={applyCredit}
                onChange={(e) => setApplyCredit(e.target.value)}
                placeholder="Amount to apply"
                className="bg-white border border-zinc-200 px-3 py-2.5 text-sm w-full text-zinc-900 placeholder:text-zinc-400 rounded-sm focus:border-teal-600 outline-none"
                data-testid="apply-credit-input"
              />
            </div>
          )}

          <div>
            <h2 className="font-heading text-xs uppercase tracking-wide mb-4 text-teal-700">Coupon Code</h2>
            <div className="flex gap-2">
              <input value={couponCode} onChange={(e) => setCouponCode(e.target.value)} placeholder="Enter code" className="bg-white border border-zinc-200 px-3 py-2.5 text-sm flex-1 text-zinc-900 placeholder:text-zinc-400 rounded-sm focus:border-teal-600 outline-none" data-testid="coupon-code-input" />
              <button type="button" onClick={applyCoupon} className="border border-zinc-200 hover:border-charcoal hover:text-charcoal text-zinc-800 px-4 py-2.5 text-xs font-heading font-bold uppercase transition-colors" data-testid="apply-coupon-button">Apply</button>
            </div>
            {appliedCoupon && <p className="text-xs text-emerald-600 mt-2" data-testid="coupon-applied-note">{appliedCoupon} applied — you save {money(discount)}</p>}
          </div>
        </div>

        <div className="border border-zinc-200 rounded-sm bg-white p-6 h-fit">
          <div className="space-y-3 text-sm mb-4 text-neutral-800">
            {items.map((i) => (
              <div key={i.product_id} className="flex justify-between items-start">
                <div>
                  <div className="font-semibold text-neutral-900">{i.brand_name}</div>
                  <div className="text-neutral-500 text-xs">{i.model}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono">{money(i.price + (i.lens_price || 0))}</div>
                  {i.original_rrp && <div className="text-xs font-mono text-neutral-400 line-through">{money(i.original_rrp)}</div>}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-zinc-100 pt-4 space-y-1.5 text-sm mb-4 text-zinc-800">
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span data-testid="checkout-subtotal">{money(subtotal + lensTotal)}</span>
            </div>
            <div className="flex justify-between text-zinc-400" data-testid="checkout-shipping-line">
              <span>Shipping</span>
              <span>{isPickup ? "Free (Pickup)" : shippingCost === 0 ? "Free" : money(shippingCost)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-emerald-600" data-testid="checkout-discount-line">
                <span>Discount</span>
                <span>-{money(discount)}</span>
              </div>
            )}
            {creditApplied > 0 && (
              <div className="flex justify-between text-emerald-600" data-testid="checkout-credit-line">
                <span>Lookers Credit</span>
                <span>-{money(creditApplied)}</span>
              </div>
            )}
            <div className="flex justify-between font-heading text-base pt-2 border-t border-zinc-100">
              <span>Total</span>
              <span data-testid="checkout-total">{money(orderTotal)}</span>
            </div>
          </div>
          <button onClick={handlePay} disabled={submitting} className="w-full bg-teal-600 hover:bg-charcoal text-white font-heading font-bold text-xs uppercase tracking-wide py-4 transition-colors disabled:opacity-50" data-testid="pay-now-button">
            {submitting ? "Processing…" : "Pay Now"}
          </button>
          <p className="text-xs text-zinc-400 mt-3">Secure checkout powered by Stripe.</p>
          <div className="mt-5 bg-teal-50 border border-teal-100 rounded-2xl p-4" data-testid="checkout-photo-reward">
            <div className="flex items-center gap-2 mb-1.5">
              <Camera size={15} className="text-teal-700" />
              <span className="text-xs font-heading font-extrabold uppercase tracking-wide text-teal-800">15% Off Your Next Pair</span>
            </div>
            <p className="text-xs text-neutral-600">
              Once they arrive, send us a photo of you wearing them and we'll email you a code for 15% off your next pair.{" "}
              <Link to="/share-your-look" className="underline font-semibold text-teal-800" data-testid="checkout-photo-reward-link">How it works</Link>
            </p>
          </div>
          <div className="mt-4 pt-4 border-t border-neutral-100 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] font-heading font-bold uppercase tracking-wider text-neutral-400" data-testid="checkout-trust-strip">
            <span className="text-teal-600">Authenticity Checked</span>
            <span className="text-neutral-300" aria-hidden="true">·</span>
            <span>Condition Disclosed</span>
            <span className="text-neutral-300" aria-hidden="true">·</span>
            <span>Secure Checkout</span>
          </div>
        </div>
      </div>
    </div>
  );
}
